import {
    cloneDeep,
    flowRight,
    get,
    noop,
    debounce,
} from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Sticky, StickyContainer } from 'react-sticky';
import styled, { css, withTheme } from 'styled-components';
import scrollToElement from 'scroll-to-element';

import { white, grey25 } from '@zocdoc/branding/colors';
import { kievitSlabBook, FontFamily } from '@zocdoc/components/src/typography';
import { breakpoint, getWindowWidth, Breakpoints } from '@zocdoc/components/src/grid';
import { CARRIER } from '@zocdoc/components/src/InsuranceForm/constants';
import { Page, PaddedContainer } from '@zocdoc/components/src/Layout';
import { FooterWithMetrics } from '@zocdoc/components/src/Layout/Footer';
import { HeaderWithMetrics } from '@zocdoc/components/src/Layout/Header';
import { Link } from '@zocdoc/components/src/link';
import { SeoBreadcrumbs, SeoRelatedLinks } from '@zocdoc/components/src/Seo';
import { Tabs, Tab } from './Tabs';
import { UniversalSearchHeader } from '@zocdoc/components/src/UniversalSearchHeader';
import isMobileOrTabletBreakpoint from '@zocdoc/components/src/utils/isMobileOrTabletBreakpoint';
import { setInsuranceType } from '@zocdoc/components/src/InsurancePicker';

import { withAB } from '../../../config/ab';
import TrackingPixel from '../../../config/tracking';
import { TARGETS, EVENT_ACTION } from '../../../EventTrackingConstants';
import CarouselMap from '../../../Shared/CarouselMap';
import { CrazyEgg } from '../../../Shared/CrazyEgg';
import InfoTooltip from '../../../Shared/InfoTooltip/view';
import MobileHeaderToggles from '../../../Shared/MobileHeaderToggles';
import { withSpotlightEnabled } from '../../../Shared/Overlay';
import isADentistrySpecialty from '../../../utils/isADentistrySpecialty';
import { Sections, PROVIDER_STATUS } from '../constants';
import {
    SHOW_REVIEW_COUNT,
    SHOW_NEW_SIDE_BAR,
    PINA_HAMBURGER_MENU_WELL_GUIDE_LINK,
    PCE_TRACKING_ID_DUMMY,
} from '../../../config/ab/experiments';
import { isEligibleForUniversalSearchHeader } from '../utils/isEligibleForUniversalSearchHeader';
import BookingColumnWithoutSpotlight from './BookingColumn';
import BookingSectionButton from './BookingSectionButton';
import { LOCALIZED_STRINGS } from './constants';
import Header from './Header';
import PictureGallery from './PictureGallery';
import Locations from './ProfileLocations';
import ProfileTab from './ProfileTab';
import Reviews from './Reviews';
import OptOutReviews from './Reviews/OptOutReviews';
import { CrossPageActionTracker } from '../../../Shared/CrossPageActionTracking';
import { getWellGuideLinkVariantFromProps } from '../../../config/ab/selector';
import * as fullstory from '../../../utils/fullstory';
import { Metrics, MetricsProvider, MetricsClickable, CustomMetrics } from '@zocdoc/frontend-metrics';
import { createPageCategory } from '../analytics';
import {
    CheckInsuranceModal,
    isCheckInsuranceModalAlreadyShownInCurrentSession,
    markCheckInsuranceModalShownInCurrentSession,
} from '../../../Shared/Modals/checkInsuranceModal';

const BookingColumnWithSpotlight = withSpotlightEnabled(BookingColumnWithoutSpotlight);
const contentTypeface = kievitSlabBook(16, 24);
const desktopContentTypeface = kievitSlabBook(18, 24);

export class ProfilePage extends PureComponent {
    static propTypes = {
        ab: PropTypes.shape({
            experimentId: PropTypes.string,
            defaultAssignment: PropTypes.string,
            isFeatureFlag: PropTypes.bool,
        }),
        bookingSpotlightIsOn: PropTypes.bool,
        carrierId: PropTypes.string,
        clearCarrierAndPlan: PropTypes.func.isRequired,
        deletePreviewProvider: PropTypes.func,
        deletePreviewProviderFailed: PropTypes.bool,
        deletePreviewProviderSucceeded: PropTypes.bool,
        deletingPreviewProvider: PropTypes.bool,
        initParameters: PropTypes.object,
        insurancePickerHasBeenOpened: PropTypes.bool,
        openInsurancePicker: PropTypes.func,
        pageId: PropTypes.string,
        planId: PropTypes.string,
        provider: PropTypes.object,
        searchQueryGuid: PropTypes.string,
        sessionId: PropTypes.string,
        setupHeaderStoreSubscription: PropTypes.func.isRequired,
        trackingId: PropTypes.string,
        trackSignInLinkClickConversion: PropTypes.func.isRequired,
        turnBookingSpotlightOff: PropTypes.func.isRequired,
        turnBookingSpotlightOn: PropTypes.func.isRequired,
    };

    static defaultProps = {
        initParameters: {
            doctorLanguageId: '-1',
            procedureId: '-1',
            isNewPatient: true,
        },
        openInsurancePicker: noop,
        provider: {},
    };

    state = {
        canShowCheckInsuranceModal: false,
        checkInsuranceModalShown: false,
    };

    tabs = null;
    setTabIndex = noop;
    isSticky = null;
    previousScrollTop = 0; // keep track of where the user scrolled to on the page previously
    hasScrolled = false;

    componentDidMount() {
        const {
            provider,
            setupHeaderStoreSubscription,
        } = this.props;

        setupHeaderStoreSubscription();

        if (provider.isPreview) {
            fullstory.setUserVars({ previewProfilePageView: 'true' });
        }

        if (provider.status === PROVIDER_STATUS.Churned) {
            fullstory.setUserVars({ churnedProfilePageView: 'true' });
        }

        if (isCheckInsuranceModalAlreadyShownInCurrentSession()) {
            this.setState({
                checkInsuranceModalShown: true,
            });
        } else {
            markCheckInsuranceModalShownInCurrentSession();
        }

        window.addEventListener('scroll', this.handleScroll, false);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll, false);
    }

    render() {
        const {
            bookingSpotlightIsOn,
            canDeletePreviewProfiles,
            deletePreviewProviderFailed,
            deletePreviewProviderSucceeded,
            deletingPreviewProvider,
            hideMap,
            initParameters,
            is1Z,
            mapIsActive,
            provider,
            selectedLocationId,
            showFindSimilarDoctors,
            turnBookingSpotlightOff,
            turnBookingSpotlightOn,
            pageId,
        } = this.props;

        const renderAsideAsModal = !is1Z;

        const currentLocationCloudId = selectedLocationId
            || initParameters.locationCloudId
            || get(provider, 'locations[0].id', '');

        const canShowNewExisting = this.shouldShowNewExisting();
        const BookingColumn = provider.isPreview && showFindSimilarDoctors
            ? BookingColumnWithoutSpotlight
            : BookingColumnWithSpotlight;

        if (deletePreviewProviderSucceeded) {
            return (
                <p>Preview provider deleted successfully.</p>
            );
        }

        return (
            <MetricsProvider getPageId={() => pageId}>
                <Metrics
                    businessUnit="PatientWebApp"
                    category="Profile"
                    page="provider profile"
                    zlogPageType={createPageCategory(provider)}
                    zlogRollupBucket="Profile"
                >
                    <StyledPage>
                        <CrossPageActionTracker />

                        <Metrics section="PageHeader">
                            {this.renderHeader()}

                            <MetricsClickable
                                action="Click Mobile Header View Map"
                                component="MobileHeaderViewMap"
                                element="Link"
                            >
                                <MobileHeaderToggles onClick={this.handleClickToggleMap}>
                                    <FormattedMessage
                                        defaultMessage="View map"
                                        id="Profile.Header.MapToggle"
                                    />
                                </MobileHeaderToggles>
                            </MetricsClickable>
                        </Metrics>

                        <StyledStickyContainer>
                            <Metrics section="Profile">
                                <ProfilePageWrapper
                                    data-test="profilePageWrapper"
                                    itemScope
                                    itemType={`http://schema.org/${this.getSchemaTypeForProvider(provider)}`}
                                >
                                    <Metrics component="HeaderNameCard">
                                        <Header
                                            canDeletePreviewProfiles={canDeletePreviewProfiles}
                                            deletePreviewProviderFailed={deletePreviewProviderFailed}
                                            deletePreviewProviderSucceeded={deletePreviewProviderSucceeded}
                                            deletingPreviewProvider={deletingPreviewProvider}
                                            onDeletePreviewProvider={this.handleDeletePreviewProvider}
                                            onReviewLinkClick={this.handleClickReviewLink}
                                            onReviewStarsClick={this.handleClickReviewStarsClick}
                                            provider={provider}
                                            shiftPictureUp={canShowNewExisting}
                                            showGallery={this.showGallery}
                                            showReviewsLink={is1Z}
                                        />
                                    </Metrics>

                                    <StyledAside
                                        asModal={renderAsideAsModal}
                                        bookingSpotlightIsOn={bookingSpotlightIsOn}
                                    >
                                        <BookingColumn
                                            currentLocationCloudId={currentLocationCloudId}
                                            onClickSpotlightOverlay={turnBookingSpotlightOff}
                                            onClickWrappedComponent={turnBookingSpotlightOn}
                                            showNewExistingToggle={canShowNewExisting}
                                            spotlightIsOn={bookingSpotlightIsOn}
                                            {...this.props}
                                        />
                                    </StyledAside>

                                    <ProfileStickyContainer withBorder={renderAsideAsModal}>
                                        <Sticky disableCompensation>
                                            {stickyProps => this.renderProfileTabs(stickyProps)}
                                        </Sticky>
                                    </ProfileStickyContainer>

                                    {this.maybeRenderSeoLinkFarm()}
                                    {this.maybeRenderSeoBreadcrumbs()}
                                    {this.maybeRenderCheckInsuranceModal()}

                                </ProfilePageWrapper>
                            </Metrics>

                            <PictureGallery name={provider.approvedFullName} />

                            {mapIsActive ? (
                                <CarouselMap
                                    hideMap={hideMap}
                                    label={provider.approvedFullName}
                                    locationId={currentLocationCloudId}
                                    locations={provider.locations}
                                    providerId={provider.id}
                                />
                            ) : null}

                            {this.maybeRenderStickyBookAppointmentButton()}
                        </StyledStickyContainer>

                        <FooterWithMetrics />

                        <TrackingPixel />
                        <CrazyEgg
                            DesktopSnapshotName="1Z Profile - Desktop"
                            MobileSnapshotName="1Z Profile - MobileWeb"
                            TabletSnapshotName="1Z Profile - Tablet"
                        />
                    </StyledPage>
                </Metrics>
            </MetricsProvider>
        );
    }

    getSchemaTypeForProvider(provider) {
        return isADentistrySpecialty(get(provider, 'relevantSpecialty.id')) ? 'Dentist' : 'Physician';
    }

    maybeRenderStickyBookAppointmentButton() {
        if (this.props.is1Z) return;

        return (
            <BookingAppointmentButtonContainer>
                <Sticky
                    disableCompensation
                    topOffset={-200}
                >
                    {
                        ({ style, distanceFromBottom, calculatedHeight }) => {
                            let styleClone = {};
                            const windowInnerHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
                            const realDistanceFromBottom = (distanceFromBottom + calculatedHeight) - windowInnerHeight;
                            if (realDistanceFromBottom > 0) {
                                styleClone = cloneDeep(style);
                                styleClone.background = 'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.75) 20px, rgba(255, 255, 255, 1))';
                                delete styleClone.top;
                                styleClone.bottom = 0;
                            }

                            return (
                                <div style={styleClone}>
                                    <BookingSectionButton provider={this.props.provider} />
                                </div>
                            );
                        }
                    }
                </Sticky>
            </BookingAppointmentButtonContainer>
        );
    }

    renderLocations() {
        const {
            expandedLocations,
            provider,
            providerId,
            showMap,
            theme,
            intl,
        } = this.props;

        const { iBubblesText } = theme.profile;

        const locationTooltipMessage = localizedTooltips(iBubblesText)[Sections.OfficeLocations];
        const tooltipTextIsDefined = locationTooltipMessage.defaultMessage;

        return (
            <ContentContainer>
                <Header3>
                    <FormattedMessage
                        defaultMessage="Office locations"
                        id="Profile.Page.Components.OfficeLocations"
                    />
                    {tooltipTextIsDefined && (
                        <CustomMetrics>
                            {
                                ({ sendMetric }) => {
                                    const onDisplayTooltip = debounce(() => sendMetric({
                                        component: 'IBubble',
                                        element: 'IBubble',
                                        action: 'Click I Bubble Office Locations',
                                    }),
                                    3000,
                                    {
                                        leading: true,
                                        trailing: false,
                                    });

                                    return (
                                        <InfoTooltip
                                            alt={intl.formatMessage(locationTooltipMessage)}
                                            onDisplayTooltip={onDisplayTooltip}
                                        >
                                            {intl.formatMessage(locationTooltipMessage)}
                                        </InfoTooltip>
                                    );
                                }
                            }
                        </CustomMetrics>
                    )}
                </Header3>
                {provider.locations && provider.locations.length
                    ? (
                        <Locations
                            expandedLocations={expandedLocations}
                            expandLocation={this.handleToggleLocationExpanded}
                            locations={provider.locations}
                            onClickExpandMap={showMap}
                            providerId={providerId}
                            showMap={this.handleClickExpandMap}
                        />
                    )
                    : <FormattedMessage {...LOCALIZED_STRINGS.NoOfficeLocations} />
                }
            </ContentContainer>
        );
    }

    renderProfileTabs({ style, distanceFromTop, isSticky }) {
        const { provider } = this.props;
        let stickyStyle;
        this.isSticky = isSticky;

        if (isMobileOrTabletBreakpoint() && distanceFromTop <= 0) {
            stickyStyle = {
                position: 'fixed',
                top: 0,
                background: '#fff',
                width: '100%',
                zIndex: 2,
            };
        }

        const tabHeight = this.tabs ? this.tabs.querySelector('[data-test="tab-link-holder"]').offsetHeight : null;

        return (
            <Tabs
                data-test="profile-tabs"
                innerRef={(node, setTabIndex) => { this.tabs = node; this.setTabIndex = setTabIndex; }}
                isSticky={isSticky}
                onTabClick={this.handleClickTab}
                style={stickyStyle || style}
                tabHeight={tabHeight}
            >
                <Tab
                    data-test="profile-tab"
                    metricsElement="Profile"
                    title={<FormattedMessage {...LOCALIZED_STRINGS.ProfileTabLabel} />}
                >
                    <TabContentContainer>
                        <ProfileTab provider={provider} />
                    </TabContentContainer>

                    {this.renderLocations()}
                </Tab>
                {this.maybeRenderReviewsTab()}
            </Tabs>
        );
    }

    renderHeader() {
        const {
            carrierId,
            pageId,
            planId,
            provider,
            searchQueryGuid,
            sessionId,
            theme,
            trackingId,
            trackSignInLinkClickConversion,
        } = this.props;

        const universalSearchHeaderProps = {
            carrierId,
            pageId,
            planId,
            referrerPageType: this.getReferrerPageType(),
            searchQueryGuid,
            sessionId,
            setInsuranceType,
            trackingId,
        };

        return isEligibleForUniversalSearchHeader(theme.directoryId, provider.isPreview, provider.status) ?
            (
                <UniversalSearchHeader
                    data-uem-id="zd-preview-profile-universal-search"
                    {...universalSearchHeaderProps}
                    useSignInDropdown
                />
            ) : (
                <HeaderWithMetrics
                    showNavigationLinks
                    trackSignInLinkClickConversion={trackSignInLinkClickConversion}
                    useSignInDropdown
                    wellGuideLinkVariant={getWellGuideLinkVariantFromProps(this.props)}
                />
            );
    }

    maybeRenderReviewsTab() {
        const { provider } = this.props;
        const hasReviews = provider.reviews && provider.reviews.length > 0;

        if (hasReviews || provider.optOutOfRatings) {
            const title = <FormattedMessage {...LOCALIZED_STRINGS.ReviewsTabLabel} />;
            return (
                <Tab
                    data-test="review-tab"
                    metricsElement="Reviews"
                    title={title}
                >
                    {provider.optOutOfRatings
                        ? <OptOutReviews />
                        : <Reviews
                            rating={provider.averageRating}
                            reviewCount={provider.reviewCount}
                            reviews={provider.reviews}
                        />}
                </Tab>
            );
        } else {
            return <div />;
        }
    }

    maybeRenderSeoLinkFarm() {
        if (this.props.seoRelatedLinks && this.props.theme.components.SeoRelatedLinks.enabled) {
            return (
                <SeoRelatedLinksFooter>
                    <SeoRelatedLinks
                        data-test="profile-seo-links"
                        sections={this.props.seoRelatedLinks.sections}
                    />
                </SeoRelatedLinksFooter>
            );
        }
    }

    maybeRenderSeoBreadcrumbs() {
        if (this.props.seo && this.props.seo.breadcrumbs) {
            return (
                <SeoBreadcrumbsFooter>
                    <Metrics
                        action="Click Related Links Breadcrumb"
                        section="RelatedLinks"
                    >
                        <SeoBreadcrumbs links={this.props.seo.breadcrumbs} />
                    </Metrics>
                </SeoBreadcrumbsFooter>
            );
        }
    }

    shouldShowCheckInsuranceModal() {
        const {
            carrierId,
            insurancePickerHasBeenOpened,
        } = this.props;
        const {
            canShowCheckInsuranceModal,
            checkInsuranceModalShown,
        } = this.state;
        return canShowCheckInsuranceModal
            && !checkInsuranceModalShown
            && !insurancePickerHasBeenOpened
            && (carrierId === null
                || carrierId === undefined
                || carrierId === CARRIER.CARRIER_NOT_CHOSEN
                || carrierId === CARRIER.ILL_CHOOSE_LATER);
    }

    maybeRenderCheckInsuranceModal() {
        const { provider } = this.props;
        if (this.shouldShowCheckInsuranceModal()) {
            return (
                <CheckInsuranceModal
                    onClickCancel={this.handleClickCloseCheckInsuranceModal}
                    onClickCheckMyInsurance={this.handleClickCheckMyInsurance}
                    providerName={provider.nameInSentence}
                />
            );
        }
    }

    handleScroll = () => {
        if (!this.hasScrolled) {
            this.hasScrolled = true;
            setTimeout(() => {
                this.setState({
                    canShowCheckInsuranceModal: true,
                });
            },
            3000);
        }
    }

    handleClickCheckMyInsurance = () => {
        const {
            clearCarrierAndPlan,
            openInsurancePicker,
            turnBookingSpotlightOn,
        } = this.props;
        this.setState({
            checkInsuranceModalShown: true,
        });

        scrollToElement(document.body, {
            duration: 500,
            ease: 'in-out-circ',
        });

        turnBookingSpotlightOn();
        openInsurancePicker();
        clearCarrierAndPlan();
    }

    handleClickCloseCheckInsuranceModal = () => {
        this.setState({
            checkInsuranceModalShown: true,
        });
    }

    scrollToTabs() {
        scrollToElement(this.tabs, {
            duration: 500,
            ease: 'in-out-circ',
        });
    }

    handleClickReviewStarsClick = () => {
        this.setTabIndex(1);
        if (getWindowWidth() < Breakpoints.medium) {
            this.scrollToTabs();
        }
    }

    handleClickReviewLink = () => {
        this.setTabIndex(1);
        if (getWindowWidth() < Breakpoints.medium) {
            this.scrollToTabs();
        }
    }

    showGallery = event => {
        const target = event.currentTarget;
        const actionName = target.getAttribute('data-action-name') || '';
        let elementName = 'NULL';
        if (actionName === EVENT_ACTION.CLICK_PROVIDER_PICTURE) {
            elementName = TARGETS.PROFILE_PROVIDER_PICTURE_FRAME;
        } else if (actionName === EVENT_ACTION.CLICK_ADDITIONAL_PICTURE_CAPTION_LINK) {
            elementName = TARGETS.PROFILE_ADDITIONAL_PICTURE_CAPTION_LINK;
        }
        this.props.showGallery({
            pictures: this.props.provider.smallPictures,
            action: actionName,
            Element: elementName,
            EventAction: actionName.toLowerCase(),
        });
    };

    handleClickToggleMap = () => {
        this.props.showMap();
    }

    handleClickExpandMap = (ev, loc) => {
        const {
            setLocation,
            showMap,
        } = this.props;
        setLocation(loc.id);
        showMap();
    };

    handleToggleLocationExpanded = (ev, loc) => this.props.toggleLocationExpanded(loc.id);

    handleDeletePreviewProvider = () => {
        this.props.deletePreviewProvider(
            this.props.provider.monolithId,
            this.props.provider.personIdentifierSource
        );
    };

    handleClickTab = () => {
        if (this.isSticky) {
            // ensure support for IE as well
            const currentScrollTop = (window.pageYOffset !== undefined) ? window.pageYOffset
                : (document.documentElement || document.body.parentNode || document.body).scrollTop;

            if (this.previousScrollTop) {
                window.scrollTo(0, this.previousScrollTop);
            } else {
                const node = findDOMNode(this.tabs);

                if (node) {
                    node.scrollIntoView();
                }
            }

            this.previousScrollTop = currentScrollTop;
        }
    };

    getReferrerPageType = () => {
        const {
            provider,
        } = this.props;

        if (provider.isPreview) {
            return 'profile-preview';
        } else if (provider.status === PROVIDER_STATUS.Churned) {
            return 'profile-churned';
        } else {
            return null;
        }
    }

    shouldShowNewExisting = () => {
        // New or Existing patient toggle
        const {
            is1Z,
            provider,
        } = this.props;
        return is1Z && provider && !provider.isPreview && provider.isBookable &&
            provider.status !== PROVIDER_STATUS.Locked &&
            provider.status !== PROVIDER_STATUS.Churned;
    }
}

const localizedTooltips = iBubblesText => defineMessages({
    [Sections.OfficeLocations]: {
        id: 'Profile.Page.Components.OfficeLocations',
        defaultMessage: iBubblesText[Sections.OfficeLocations],
    },
});

const StyledPage = styled(Page)`
    box-sizing: border-box;
    background-color: ${white};
`;

const ProfilePageWrapper = styled.div`
    color: ${p => p.theme.profile.textColor};

    ${Link} {
        border-bottom: 1px dotted;
        color: ${p => p.theme.profile.linkColor};

        &:hover {
            border-bottom: 1px solid;
            text-decoration: none;
        }
    }
    ${breakpoint.medium} {
        max-width: 62.5%; /* make room for aside */
    }
`;

const BookingAppointmentButtonContainer = styled.div`
    width: 100%;
    position: absolute;
    bottom: 0;
    display: block;
`;

const TabContentContainer = styled(PaddedContainer)`
    ${contentTypeface}
    color: ${p => p.theme.profile.textColor};
    padding-bottom: 20px;

    ${breakpoint.small} {
        padding-top: 16px;
    }

    ${breakpoint.medium} {
        ${desktopContentTypeface}
        padding-top: 14px;
        padding-bottom: 0;
    }
`;

const ContentContainer = styled(TabContentContainer)`
    padding-top: 12px;
`;

const Header1 = styled.h1`
    font: 20px/28px ${FontFamily.SharpSansBold};
    margin-top: 0;

    ${breakpoint.medium} {
        margin-top: 30px;
    }
`;

const Header3 = Header1.withComponent('h3');

const ProfileStickyContainer = styled(StickyContainer)`
    ${p => p.withBorder && css`
        border-top: 1px solid ${grey25};
    `}
    ${breakpoint.medium} {
        border-top: none;
    }
`;

const SeoRelatedLinksFooter = styled.div`
    background-color: ${white};
`;

const SeoBreadcrumbsFooter = styled(ContentContainer)`
    display: flex;
    align-items: center;
    height: 96px;
    background-color: white;
`;

const StyledStickyContainer = styled(StickyContainer)`
    position: relative;
`;

const StyledAside = styled.aside`
    bottom: 0;
    left: 0;
    position: ${p => p.asModal ? 'fixed' : 'static'};
    right: 0;
    top: 0;
    z-index: 10;

    ${p => p.asModal && css`
        ${breakpoint('small', { down: true })} {
            position: fixed;
            display: ${p.bookingSpotlightIsOn ? 'block' : 'none'};
        }
    `}

    ${breakpoint.medium} {
        position: absolute;
        left: 62.5%;
    }
`;

export const AB_FLAGS = [
    PCE_TRACKING_ID_DUMMY,
    PINA_HAMBURGER_MENU_WELL_GUIDE_LINK,
    SHOW_NEW_SIDE_BAR,
    SHOW_REVIEW_COUNT,
];

export default flowRight(
    withAB(AB_FLAGS),
    injectIntl,
    withTheme,
)(ProfilePage);
